// idb-schema-def.ts (Story 7.3) — PURE schema definition. The single source of schema
// truth for Franklin's offline IndexedDB database.
//
// WHY this module has NO `dexie` import (the deliberate split, mirroring 7.2's
// `caching-routes.ts` pure-helper precedent):
//   1. Schema truth stays separate from the Dexie binding (`idb-schema.ts`), so Dexie-free
//      consumers (drain-core's pure logic, plain unit tests) can reference table shapes and
//      version strings without dragging the library in.
//   2. Every open of the shared DB MUST apply byte-identical `version().stores()` declarations —
//      a different/lower schema version throws Dexie `VersionError`. One shared
//      `SCHEMA_V1`/`SCHEMA_V2` makes drift structurally impossible.
//   3. It is unit-testable with no IndexedDB runtime.
//
// PINNED to the proven R6 PoC (spencer-derisking/frontend/db.js). Franklin divergences,
// all deliberate: sync_queue → outbound_queue (rename); + cache_metadata (no PoC equivalent —
// backs offline reads + the 7.6 ledger); PoC draft body (task_ids/scores/notes) collapsed into
// an opaque `data` payload (real eval shape owned by Epic 6); `client_uuid` dropped (Franklin
// auth/clinician_id supersedes the PoC's per-browser identity); submit ops NOT queued (online-only
// per 7.7 — the PoC queued them). Keys are client-minted UUIDv4 (`&id`/`&key`), NOT auto-increment,
// exactly as the PoC proved.

/** The Franklin offline IndexedDB database name, opened by the main-thread Dexie binding (`idb-schema.ts`). */
export const DB_NAME = "franklin";

// --- `meta` keys that other modules must agree on ---------------------------------------------
// Declared HERE (the pure schema-truth module) rather than in their consuming modules, because more
// than one module must agree on each literal and a second copy of either would be a silent drift the
// type system cannot catch. (The v3 `.upgrade()` was one such reader until the 19.6 review made it a
// no-op; the keys stay here because `offline-session.ts` / `owner-scope.ts` still need them and this
// is the one module a pure consumer may import.)

/** gh-461 — the credential-free offline identity blob (`OfflineSession`). Owned by
 *  `offline-session.ts`; DELETED on genuine expiry / explicit sign-out. */
export const AUTH_SESSION_KEY = "auth_session";

/**
 * Story 19.6 Unit E — the durable DEVICE identity: just the `AuthUser.userId` of the clinician
 * who last held a validated session on this device. Owned by `owner-scope.ts`.
 *
 * WHY a second key rather than reusing `auth_session`: `clearOfflineIdentity()` deletes
 * `auth_session` on sign-out and on every expiry path, while the drafts it describes deliberately
 * SURVIVE — so `auth_session` cannot answer "whose rows are these?" for the very device state that
 * needs the answer. Session lifetime and device-identity lifetime are two separate facts; this key
 * is the second one, and it is NEVER deleted (not on sign-out, not on expiry, not on user change).
 */
export const OWNER_IDENTITY_KEY = "owner_identity";

/**
 * Franklin#867 Part A — durable "this device was explicitly signed out" marker. Owned by
 * `offline-session.ts`. Set on every explicit `signOut`; cleared only on interactive `setSession`.
 * While set, boot must not silent-refresh into the previous clinician even if the HttpOnly refresh
 * cookie is still live (offline sign-out cannot revoke it). Part B (queued server revoke) is a
 * separate follow-up; this key is local-only.
 */
export const SIGNED_OUT_ON_DEVICE_KEY = "signed_out_on_device";

/**
 * Story 19.6 Unit E — the row-level owner stamp shared by `drafts`, `outbound_queue` and
 * `cache_metadata`: the `AuthUser.userId` (JWT `sub`) of the clinician whose work the row holds.
 * `userId` and not `email` — an email is mutable (Story 14.10 self-service change) and would
 * silently orphan a device's rows the moment a clinician changed theirs.
 *
 * OPTIONAL + NON-INDEXED, exactly like `DraftRecord.client_id` above: no store string changes, no
 * fixture churn, and pre-v3 rows genuinely lack it rather than carrying a lie. The v3 `.upgrade()`
 * deliberately attributes NOTHING (`upgradeToV3`), so a pre-v3 row stays `undefined`: hidden from
 * every read and excluded from the drain — never deleted. It is attributed later, and only where
 * ownership is PROVEN for that row — a 200 GET under the clinician's token, or that clinician's own
 * save (`adoptUnattributedEvaluationRows`) — and an already-owned row is never re-attributed.
 *
 * This is CACHE HYGIENE / device-level data isolation on a shared device, NOT an access-control
 * boundary: the frontend is a display layer (rule-06) and the real guarantee is Postgres RLS plus
 * the endpoint's own authorization, both already in place and untouched by this field.
 */
export interface OwnedRecord {
  owner?: string;
}

/**
 * Does `row` belong to `owner`? The meaning of the `owner` field, so it lives beside the field —
 * and in this PURE module, so the pure consumers (`drain-core`, `sync-ledger`) can scope their reads
 * without importing Dexie. `owner-scope.ts` re-exports it for the Dexie-side readers.
 *
 * Fails closed in BOTH directions:
 *   - a row with NO `owner` (a pre-v3 row the v3 `.upgrade()` deliberately did not guess at) belongs
 *     to NOBODY: it is hidden from everyone rather than adopted by whoever signs in next;
 *   - an UNRESOLVED current owner (`undefined`) matches nothing, so a failed identity read hides
 *     every row rather than showing every row.
 */
export function isOwnedBy(row: OwnedRecord | undefined | null, owner: string | undefined): boolean {
  if (!row || !owner) return false;
  return row.owner === owner;
}

/** Keep only `owner`'s rows. Drops rows from the RESULT, never from the store — this unit deletes
 *  nothing (see `OWNER_IDENTITY_KEY` / `owner-scope.ts` for why). */
export function filterOwned<T extends OwnedRecord>(rows: T[], owner: string | undefined): T[] {
  return rows.filter((row) => isOwnedBy(row, owner));
}

/** A single row, or `undefined` when it is not `owner`'s — the point-read twin of `filterOwned`. */
export function ownedRow<T extends OwnedRecord>(
  row: T | undefined,
  owner: string | undefined,
): T | undefined {
  return isOwnedBy(row, owner) ? row : undefined;
}

/**
 * The server's authoritative snapshot, echoed in a 409 (`EVAL-002`) or 410 (`EVAL-009`) body under
 * `ApiError.details.server_state`. `draft_data` is the full server draft (never null —
 * `server_default '{}'`), so the resolver (7.5) diffs it with no extra GET. Defined alongside the
 * schema (not in drain-core) so `ConflictBlock` carries the typed shape and drain-core needs no cast.
 */
export interface ServerState {
  version: number;
  state: string;
  draft_data: Record<string, unknown>;
}

/** Conflict block — populated by drain-core (7.4) on a 409; consumed by the resolver (7.5). Inert in 7.3. */
export interface ConflictBlock {
  server_state: ServerState; // full server snapshot from the 409/410 body (no extra GET)
  detected_at: string; // ISO 8601
}

export interface DraftRecord extends OwnedRecord {
  id: string; // PK — client-minted crypto.randomUUID() (PoC: &id)
  // Client (patient) UUID the eval is for. Persisted at CREATE (`startEvaluationDraft` already
  // receives it) and back-filled on a successful online GET, so an OFFLINE reopen can reconstruct a
  // usable `EvaluationDetail` from this row alone (the name-reveal flow keys off it — gh-459).
  // OPTIONAL + NON-INDEXED → a pure interface addition: no Dexie schema-version bump, no fixture churn.
  client_id?: string;
  version: number; // server-acknowledged version; 0 = never-synced sentinel (PoC)
  last_synced_version: number | null; // null until first successful drain (7.4)
  state: "draft" | "submitted"; // "submitted" is terminal — server returns 410 on later writes
  conflict: ConflictBlock | null; // null = clean; set on 409 by 7.4, read by 7.5
  data: Record<string, unknown>; // eval payload — REPLACES PoC task_ids/scores/notes; shape owned by Epic 6
  created_at: string; // ISO 8601
  updated_at: string; // ISO 8601
}

export interface OutboundOp extends OwnedRecord {
  id: string; // PK — client-minted UUID (PoC &id; NOT ++id auto-increment)
  eval_id: string; // FK → DraftRecord.id; indexed so a new edit collapses the pending op
  op: "create" | "update"; // submit is online-only in Franklin (7.7 gate) — NEVER queued (PoC queued it)
  payload: Record<string, unknown>; // latest local draft state to replay (7.4)
  client_version: number; // → X-Client-Version (PUT) / client_version (POST /sync) — sent by 7.4
  attempts: number; // retry counter (PoC bumpAttempt)
  last_error: string | null; // last replay error message (PoC bumpAttempt)
  queued_at: string; // ISO 8601 — FIFO ordering (PoC)
}

export interface CacheMetaRecord extends OwnedRecord {
  key: string; // PK — e.g. "ledger:<eval_id>" (7.6) or "cache:<resource>" (offline reads)
  value: unknown;
  updated_at: string; // ISO 8601 — freshness / staleness
}

export interface MetaEntry {
  key: string; // PK — e.g. "force_offline" (7.7 flag), "client_uuid"
  value: unknown;
}

// v1 = the working set (PoC `drafts` + renamed `outbound_queue` + Franklin-new `cache_metadata`).
// `&id`/`&key` mirror the PoC; the `&` on a PRIMARY key is redundant-but-harmless (Dexie treats the
// first token as the PK). Only indexed fields appear in the string — `version`/`payload`/`conflict`/
// `data`/`last_error` are stored unindexed.
export const SCHEMA_V1 = {
  drafts: "&id, state, updated_at",
  outbound_queue: "&id, eval_id, queued_at",
  cache_metadata: "&key, updated_at",
} as const;

// v2 ADDS the `meta` table. This is the EXACT lossless migration the R6 PoC validated
// (db.js: v1 → v2 added `meta` without disrupting existing data — the "two-version migration path
// validated" finding the report cites). Unchanged tables are restated (PoC style) so the delta is
// explicit; Dexie would also retain them if omitted. Additive only → no `.upgrade()` callback.
export const SCHEMA_V2 = {
  drafts: "&id, state, updated_at",
  outbound_queue: "&id, eval_id, queued_at",
  cache_metadata: "&key, updated_at",
  meta: "&key",
} as const;

// v3 (Story 19.6 Unit E) adds NO table and NO index — every store string below is restated
// byte-identically from v2 (PoC style, as v2 restates v1). `owner` is optional + non-indexed (the
// `client_id` precedent at the top of this file), so it needs no schema declaration at all.
//
// The bump was introduced to give Dexie a version boundary for an `owner` BACKFILL `.upgrade()`. The
// 19.6 review removed that backfill (a device-wide identity is not evidence about an individual row —
// see `upgradeToV3` in `idb-schema.ts`), so v3 now transforms nothing either. It MUST NOT be reverted
// to v2: devices have already opened this database at 3, and Dexie throws `VersionError` when a lower
// version opens a database that reached a higher one, so a "simplification" back to 2 would make the
// app unable to open its own IndexedDB on every one of those devices.
export const SCHEMA_V3 = {
  drafts: "&id, state, updated_at",
  outbound_queue: "&id, eval_id, queued_at",
  cache_metadata: "&key, updated_at",
  meta: "&key",
} as const;
